import { FC, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CoursesPage } from '@/pages/CoursesPage';
import { CategoriesPage } from '@/pages/CategoriesPage';
import { PaymentsPage } from '@/pages/PaymentsPage';
import { StripeConfigPage } from '@/pages/StripeConfigPage';
import { StudentsPage } from '@/pages/StudentsPage';
import { InstructorsPage } from '@/pages/InstructorsPage';
import { UploadPage } from '@/pages/UploadPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { GalleryPage } from '@/pages/GalleryPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SecurityPage } from '@/pages/SecurityPage';
import NotificationsPage from '@/pages/NotificationsPage';
import { ExercisesPage } from '@/pages/ExercisesPage';
import { useAuthStore } from '@/store/authStore';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: FC = () => {
  const { isAuthenticated, initializeAuth } = useAuthStore();

  useEffect(() => {
    console.log('🚀 App starting - initializing auth...');
    // Initialize authentication from localStorage when the app starts
    initializeAuth();
  }, [initializeAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />
              } 
            />
            <Route 
              path="/reset-password" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />
              } 
            />
            
            {/* Protected routes */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="courses" element={<CoursesPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="exercises" element={<ExercisesPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="payments/config" element={<StripeConfigPage />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="instructors" element={<InstructorsPage />} />
              <Route path="upload" element={<UploadPage />} />
              <Route path="gallery" element={<GalleryPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>

            {/* Unauthorized route */}
            <Route 
              path="/unauthorized" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900">No Autorizado</h1>
                    <p className="text-gray-600 mt-2">No tienes permisos para acceder a esta página</p>
                  </div>
                </div>
              } 
            />

            {/* Catch all route */}
            <Route 
              path="*" 
              element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} 
            />
          </Routes>

          {/* Toast notifications */}
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'white',
                color: 'black',
                border: '1px solid #e5e7eb',
              },
            }}
          />
        </div>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;